import { GroupAdd, MonetizationOnOutlined } from '@mui/icons-material';
import { CircularProgress, type Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import type { InsightsSummary } from '../../../../@types/contracts';
import type { InsightsDashboardMetric } from '../../../../@types/global';
import { colors } from '../../../../utils/theme';
import { DEFAULT_CURRENCY_SYMBOL } from '../../constants';
import { Loading } from '../loading/Loading';
import { parseDollars, parsePercentage, parseValue } from '../utils';

import FindingsTile from './FindingsTile';

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    margin: `0.75em 0`,
    minHeight: '10em',
    minWidth: '100%',
  },
  loading: {
    zIndex: 5,
    position: 'absolute',
  },
  revenueParent: {
    boxSizing: 'border-box',
    background: colors.LIGHT_WHITE_2,
    width: '100%',
    height: '6em',
    minWidth: '16em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: theme.spacing(0.5),
    '& .MuiSvgIcon-root': {
      marginLeft: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
      cursor: 'pointer',
      fontSize: '0.75em',
    },
  },
  textRevenue: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1.25,
    padding: theme.spacing(0, 2),
  },
  row: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    marginBottom: '0.5em',
    gap: '0.5em',
  },
  findingTilesGroup: {
    display: 'flex',
    width: 'auto',
    justifyContent: 'space-between',
  },
}));

interface Props {
  insights?: InsightsSummary;
}

const OverallPerformance = ({ insights }: Props) => {
  const classes = useStyle();
  if (!insights)
    return (
      <div className={classes.root}>
        <Loading />
      </div>
    );

  let findingRows: Array<JSX.Element> | null = null;
  const { revenue, patients, treatments, aiMetrics } = insights;

  const dashboardMetrics = [
    {
      name: 'Total Restorative Revenue',
      currency: DEFAULT_CURRENCY_SYMBOL,
      value: parseDollars(revenue?.totalRestorative),
      Icon: MonetizationOnOutlined,
      iconPath: null,
      unit: '$',
      message: 'Total Restorative Revenue, including planned treatments',
      className: 'count',
    },
    {
      name: 'Patients',
      currency: '',
      value: parseValue(patients?.totalUnique),
      Icon: GroupAdd,
      iconPath: null,
      unit: null,
      message: 'Unique Patients with Restorative Tx',
      className: 'count',
    },
    {
      name: 'Unique Patients with Restorative AI Findings',
      currency: '',
      value: parseValue(
        patients.uniqueWithAiFindings.find((e) => e.findingType === 'RESTO')
          ?.amount,
      ),
      Icon: GroupAdd,
      iconPath: null,
      unit: null,
      message:
        'Unique Patients with Restorative AI Findings. Calculated with past 30 days of data.',
      className: 'count',
    },
    {
      name: 'Patient Diagnostic Percentage',
      currency: '',
      value: parsePercentage(aiMetrics?.aiDiagnosticRate),
      Icon: null,
      iconPath: '/assets/icons/aiDiagnosisRate.svg',
      unit: '%',
      message:
        'The percentage of patients with restorative treatments benefitting from AI findings.',
      className: 'count',
    },
    {
      name: 'Treatments Presented',
      currency: '',
      value: parseValue(treatments?.totalPresented),
      Icon: null,
      iconPath: '/assets/icons/aiDiagnosisRate.svg',
      unit: null,
      message: 'New treatments presented this month',
      className: 'count',
    },
    {
      name: 'Treatments Completed',
      currency: '',
      value: parseValue(treatments?.totalCompleted),
      Icon: null,
      iconPath: '/assets/icons/aiDiagnosisRate.svg',
      unit: null,
      message: 'Treatments completed this month',
      className: 'count',
    },
    {
      name: 'Case Completion (Counts)',
      currency: '',
      value: parsePercentage(treatments?.caseCompletionRateQuantity),
      Icon: null,
      iconPath: '/assets/icons/aiDiagnosisRate.svg',
      unit: '%',
      message: 'Case completion percentage by Tx count',
      className: 'calculation',
    },
    {
      name: 'Case Completion (Dollars)',
      currency: '',
      value: parsePercentage(treatments?.caseCompletionRateRevenue),
      Icon: null,
      iconPath: '/assets/icons/aiDiagnosisRate.svg',
      unit: '%',
      message: 'Case completion percentage by total revenue',
      className: 'calculation',
    },
  ];

  findingRows =
    !!dashboardMetrics &&
    dashboardMetrics.map((dashboardMetric: InsightsDashboardMetric, index) => (
      <FindingsTile
        dashboardMetric={dashboardMetric}
        key={`$dashboardNumber-${index}`}
      />
    ));

  const loading = (
    <div className={classes.loading}>
      <CircularProgress />
    </div>
  );

  return (
    <div className={classes.root}>
      {!findingRows && loading}
      {!!findingRows && (
        <>
          <div className={classes.row}>
            {findingRows && findingRows.slice(0, 4)}
          </div>
          <div className={classes.row}>
            {findingRows && findingRows.slice(4, 8)}
          </div>
        </>
      )}
    </div>
  );
};

export default OverallPerformance;
